import { jsPDF } from 'jspdf';

export interface CustomGroupReportAnalyticsData {
  customGroup: { _id: string; name: string; description: string };
  overview: { totalGroups: number; totalMembers: number; uniqueMembers: number; avgGroupSize: number };
  allGroups: Array<{
    _id: string;
    name: string;
    description?: string;
    participantsCount: number;
    accountId?: any;
    participants?: any[];
  }>;
}

/**
 * Generates and downloads the Custom Group report PDF.
 * Returns the file name on success; throws on error.
 */
export function generateCustomGroupReportPdf(analyticsData: CustomGroupReportAnalyticsData): string {
  const now = new Date();
  const reportDateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const reportTimeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const customGroupName = analyticsData.customGroup.name;
  const noOfGroups = analyticsData.overview.totalGroups;
  const noOfMembers = analyticsData.overview.totalMembers;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Professional margins and spacing
  const marginLeft = 25;
  const marginRight = 25;
  const marginTop = 25;
  const marginBottom = 25;
  const sectionGap = 8;
  const lineHeight = 8;
  const cellPaddingH = 6;
  const cellPaddingV = 4; // vertical padding (top/bottom) inside table cells
  const rowHeight = 12; // taller rows so text has padding above and below
  const headerRowHeight = 12;
  const labelValueGap = 5; // mm gap between label (e.g. "Date : ") and value

  let yPosition = marginTop;

  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - marginBottom) {
      pdf.addPage();
      yPosition = marginTop;
      return true;
    }
    return false;
  };

  const getTextWidth = (text: string, fontSize?: number) => {
    if (fontSize) pdf.setFontSize(fontSize);
    return (pdf as any).getTextWidth?.(text) ?? text.length * 2.5;
  };

  const contentWidth = pageWidth - marginLeft - marginRight;

  // Helper: draw text with wrapping, returns new y position
  const drawWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number, lineSpacing = 7) => {
    pdf.setFontSize(fontSize);
    const lines = (pdf as any).splitTextToSize?.(text, maxWidth) ?? [text];
    lines.forEach((line: string, i: number) => {
      pdf.text(line, x, y + i * lineSpacing);
    });
    return y + lines.length * lineSpacing;
  };

  const renderTable = (headers: string[], rows: string[][], columnWidths: number[]) => {
    let currentY = yPosition;
    const tableWidth = pageWidth - marginLeft - marginRight;
    const totalColumnWidth = columnWidths.reduce((sum, w) => sum + w, 0);
    const scaleFactor = tableWidth / totalColumnWidth;
    const scaledWidths = columnWidths.map(w => w * scaleFactor);

    // Header row: draw each cell with explicit fill and text so all headers are visible
    let xPos = marginLeft;
    for (let colIndex = 0; colIndex < headers.length; colIndex++) {
      const w = scaledWidths[colIndex];
      pdf.setFillColor(14, 160, 133);
      pdf.setDrawColor(180, 180, 180);
      pdf.rect(xPos, currentY, w, headerRowHeight, 'FD');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      const headerText = headers[colIndex];
      const textY = currentY + cellPaddingV + 3.5;
      pdf.text(headerText, xPos + cellPaddingH, textY);
      xPos += w;
    }
    currentY += headerRowHeight;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');

    // Data rows
    rows.forEach((row, rowIndex) => {
      if (currentY + rowHeight > pageHeight - marginBottom) {
        pdf.addPage();
        currentY = marginTop;
        // Redraw header on new page
        xPos = marginLeft;
        for (let colIndex = 0; colIndex < headers.length; colIndex++) {
          const w = scaledWidths[colIndex];
          pdf.setFillColor(14, 160, 133);
          pdf.setDrawColor(180, 180, 180);
          pdf.rect(xPos, currentY, w, headerRowHeight, 'FD');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text(headers[colIndex], xPos + cellPaddingH, currentY + cellPaddingV + 3.5);
          xPos += w;
        }
        currentY += headerRowHeight;
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
      }

      const rowFillColor = rowIndex % 2 === 0 ? [248, 248, 248] : [255, 255, 255];
      xPos = marginLeft;
      row.forEach((cell, colIndex) => {
        const w = scaledWidths[colIndex];
        pdf.setFillColor(rowFillColor[0], rowFillColor[1], rowFillColor[2]);
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(xPos, currentY, w, rowHeight, 'FD');
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const maxChars = colIndex === 1 ? 52 : 12;
        const displayText = cell.length > maxChars ? cell.substring(0, maxChars - 3) + '...' : cell;
        const textY = currentY + cellPaddingV + 3.5;
        pdf.text(displayText, xPos + cellPaddingH, textY);
        xPos += w;
      });
      currentY += rowHeight;
    });

    return currentY;
  };

  // --- Page heading (H1, center): Row1 = "Custom Group Summary Report :", Row2 = (Custom Group Name) with wrap ---
  const headingFontSize = 22;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(headingFontSize);
  pdf.setTextColor(14, 160, 133);
  const headingLineSpacing = 7;

  pdf.text('Custom Group Summary Report :', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += headingLineSpacing;

  const groupNameInParens = `(${customGroupName})`;
  const nameLines = (pdf as any).splitTextToSize?.(groupNameInParens, contentWidth) ?? [groupNameInParens];
  nameLines.forEach((line: string, i: number) => {
    pdf.text(line, pageWidth / 2, yPosition + i * headingLineSpacing, { align: 'center' });
  });
  yPosition += nameLines.length * headingLineSpacing;

  // --- Info in tabular format: 2 rows, 4 columns, no header ---
  const infoTableWidth = pageWidth - marginLeft - marginRight;
  const infoColWidths = [48, 42, 42, 38];
  const infoScale = infoTableWidth / infoColWidths.reduce((a, b) => a + b, 0);
  const infoScaledWidths = infoColWidths.map(w => w * infoScale);
  const infoRows: string[][] = [
    ['Report As on Date', reportDateStr, 'Time', reportTimeStr],
    ['No Of Groups', String(noOfGroups), 'No of Members', noOfMembers.toLocaleString()],
  ];
  let infoY = yPosition;
  infoRows.forEach((row, rowIndex) => {
    const rowFill = rowIndex % 2 === 0 ? [248, 248, 248] : [255, 255, 255];
    let xPos = marginLeft;
    row.forEach((cell, colIndex) => {
      const w = infoScaledWidths[colIndex];
      pdf.setFillColor(rowFill[0], rowFill[1], rowFill[2]);
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(xPos, infoY, w, rowHeight, 'FD');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', colIndex % 2 === 0 ? 'bold' : 'normal');
      const text = cell.length > 28 ? cell.substring(0, 25) + '...' : cell;
      pdf.text(text, xPos + cellPaddingH, infoY + cellPaddingV + 3.5);
      xPos += w;
    });
    infoY += rowHeight;
  });
  yPosition = infoY + sectionGap + 2.5; // gap before Group & Member List

  // --- Table: Sl No, Group Name, Member Count ---
  checkPageBreak(40);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(14, 160, 133);
  pdf.text('Group & Member List', marginLeft, yPosition);
  yPosition += lineHeight + 2;

  const tableRows = analyticsData.allGroups.map((group, index) => [
    String(index + 1),
    group.name,
    String(group.participantsCount ?? 0),
  ]);

  if (tableRows.length > 0) {
    yPosition = renderTable(
      ['Sl No', 'Group Name', 'Member Count'],
      tableRows,
      [22, 115, 43]
    ) + 10;
  } else {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('No groups in this custom group.', marginLeft, yPosition);
    yPosition += 10;
  }

  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `Page ${i} of ${pageCount} - ${customGroupName} Report`,
      pageWidth / 2,
      pageHeight - marginBottom / 2,
      { align: 'center' }
    );
  }

  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const datePrefix = `${dd}${mm}${yyyy}`;
  const safeGroupName = customGroupName.replace(/[/\\?%*:|"<>]/g, ' ').trim();
  const fileName = `${datePrefix}_CGR_${safeGroupName}.pdf`;

  pdf.save(fileName);
  return fileName;
}
